// Shared date-range utilities for the dashboard.
// All ranges return [start, end] where end is exclusive (next millisecond).

export const RANGE_OPTIONS = [
  { id: '7d', label: '7D', longLabel: 'Last 7 days' },
  { id: '30d', label: '30D', longLabel: 'Last 30 days' },
  { id: '90d', label: '90D', longLabel: 'Last 90 days' },
  { id: 'mtd', label: 'MTD', longLabel: 'Month to date' },
  { id: 'qtd', label: 'QTD', longLabel: 'Quarter to date' },
  { id: 'ytd', label: 'YTD', longLabel: 'Year to date' },
  { id: 'custom', label: 'Custom', longLabel: 'Custom range' },
];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

export function resolveRange(range, customStart, customEnd) {
  const now = new Date();
  switch (range) {
    case '7d': {
      const start = startOfDay(new Date(now.getTime() - 6 * 86400000));
      return { start, end: endOfDay(now) };
    }
    case 'mtd': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start, end: endOfDay(now) };
    }
    case '30d': {
      const start = startOfDay(new Date(now.getTime() - 29 * 86400000));
      return { start, end: endOfDay(now) };
    }
    case '90d': {
      const start = startOfDay(new Date(now.getTime() - 89 * 86400000));
      return { start, end: endOfDay(now) };
    }
    case 'ytd': {
      const start = new Date(now.getFullYear(), 0, 1);
      return { start, end: endOfDay(now) };
    }
    case 'qtd': {
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const start = new Date(now.getFullYear(), quarterStartMonth, 1);
      return { start, end: endOfDay(now) };
    }
    case 'custom': {
      if (customStart && customEnd) {
        return { start: startOfDay(customStart), end: endOfDay(customEnd) };
      }
      const start = startOfDay(new Date(now.getTime() - 29 * 86400000));
      return { start, end: endOfDay(now) };
    }
    default: {
      const start = startOfDay(new Date(now.getTime() - 29 * 86400000));
      return { start, end: endOfDay(now) };
    }
  }
}

export const formatRangeLabel = (range, customStart, customEnd) => {
  const opt = RANGE_OPTIONS.find((r) => r.id === range);
  if (!opt) return '';
  if (range === 'custom' && customStart && customEnd) {
    const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fmt(customStart)} – ${fmt(customEnd)}`;
  }
  return opt.longLabel;
};

// Stable, range-independent acquisition windows (used for KPIs that always
// reflect the calendar position of "today" regardless of the filter bar).
// US convention: weeks start on Sunday.
export function acquisitionWindows(now = new Date()) {
  const sodNow = (() => { const x = new Date(now); x.setHours(23, 59, 59, 999); return x; })();
  const wtdStart = (() => {
    const x = new Date(now);
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - x.getDay()); // back to Sunday 00:00
    return x;
  })();
  const mtdStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const ytdStart = new Date(now.getFullYear(), 0, 1);
  return {
    wtd: { start: wtdStart, end: sodNow },
    mtd: { start: mtdStart, end: sodNow },
    ytd: { start: ytdStart, end: sodNow },
  };
}

export const inRange = (date, start, end) => {
  if (!date) return false;
  const t = new Date(date).getTime();
  return Number.isFinite(t) && t >= start.getTime() && t <= end.getTime();
};

// Bucket dates by day for time-series charts. Returns ordered array of
// { label, key, count } across the inclusive range.
export function bucketByDay(items, getDate, start, end) {
  const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86400000));
  const buckets = new Map();
  for (let i = 0; i < days; i += 1) {
    const d = new Date(start.getTime() + i * 86400000);
    const key = d.toISOString().slice(0, 10);
    buckets.set(key, { key, label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), count: 0 });
  }
  items.forEach((item) => {
    const raw = getDate(item);
    if (!raw) return;
    const t = new Date(raw).getTime();
    if (!Number.isFinite(t) || t < start.getTime() || t > end.getTime()) return;
    const key = new Date(t).toISOString().slice(0, 10);
    const b = buckets.get(key);
    if (b) b.count += 1;
  });
  return Array.from(buckets.values());
}
